import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Cookie } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

const STORAGE_KEY = "urkoza_consent";

function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function applyConsent(consent) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("consent", "update", {
    ad_storage: consent.marketing ? "granted" : "denied",
    ad_user_data: consent.marketing ? "granted" : "denied",
    ad_personalization: consent.marketing ? "granted" : "denied",
    analytics_storage: consent.analytics ? "granted" : "denied",
    personalization_storage: consent.personalization ? "granted" : "denied",
    functionality_storage: "granted",
    security_storage: "granted",
  });
}

export default function CookieBanner() {
  const { t, localePath } = useI18n();
  const [visible, setVisible] = useState(false);
  const [advanced, setAdvanced] = useState(false);
  const [prefs, setPrefs] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    personalization: false,
  });

  useEffect(() => {
    const existing = readConsent();
    if (!existing) {
      setVisible(true);
    } else {
      applyConsent(existing);
    }
  }, []);

  const save = (choice) => {
    const finalPrefs = {
      necessary: true,
      analytics: choice.analytics,
      marketing: choice.marketing,
      personalization: choice.personalization,
      timestamp: new Date().toISOString(),
      version: 1,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(finalPrefs));
    applyConsent(finalPrefs);
    setVisible(false);
  };

  const acceptAll = () => save({ analytics: true, marketing: true, personalization: true });
  const rejectAll = () => save({ analytics: false, marketing: false, personalization: false });
  const savePrefs = () => save(prefs);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[80] px-4 pb-4 md:px-6 md:pb-6"
      role="dialog"
      aria-modal="false"
      aria-label={t("cookie_banner.title")}
      data-testid="cookie-banner"
    >
      <div className="mx-auto max-w-4xl bg-white border border-[#0E2A47]/15 rounded-2xl shadow-2xl overflow-hidden">
        <div className="p-5 md:p-6 flex flex-col md:flex-row gap-5 md:items-start">
          <div className="shrink-0 w-11 h-11 rounded-full bg-[#0E2A47]/5 flex items-center justify-center">
            <Cookie className="w-5 h-5 text-[#0E2A47]" aria-hidden="true" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-[#0E2A47]" data-testid="cookie-banner-title">
              {t("cookie_banner.title")}
            </h3>
            <p className="mt-2 text-sm text-[#0E2A47]/75 leading-relaxed">
              {t("cookie_banner.description")}{" "}
              <Link
                to={localePath("/cookies")}
                className="text-[#0F5F3F] underline underline-offset-2"
                data-testid="cookie-banner-policy-link"
              >
                {t("cookie_banner.policy_link")}
              </Link>
              .
            </p>
            {advanced && (
              <div className="mt-4 border-t border-[#0E2A47]/10 pt-4 grid gap-3" data-testid="cookie-banner-advanced">
                {[
                  { key: "necessary", disabled: true },
                  { key: "analytics", disabled: false },
                  { key: "marketing", disabled: false },
                  { key: "personalization", disabled: false },
                ].map((row) => (
                  <label
                    key={row.key}
                    className="flex items-start justify-between gap-4 p-3 bg-[#F5F6F8] rounded-lg"
                  >
                    <div>
                      <div className="text-sm font-medium text-[#0E2A47]">
                        {t(`cookie_banner.categories.${row.key}`)}
                      </div>
                      <div className="text-xs text-[#0E2A47]/60 mt-1">
                        {t(`cookie_banner.categories.${row.key}_desc`)}
                      </div>
                    </div>
                    <input
                      type="checkbox"
                      className="mt-1 h-5 w-5 accent-[#0F5F3F]"
                      checked={row.disabled ? true : prefs[row.key]}
                      disabled={row.disabled}
                      onChange={(e) =>
                        setPrefs((p) => ({ ...p, [row.key]: e.target.checked }))
                      }
                      data-testid={`cookie-toggle-${row.key}`}
                    />
                  </label>
                ))}
              </div>
            )}
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={acceptAll}
                className="px-5 py-2.5 rounded-full bg-[#0F5F3F] text-white text-sm font-medium hover:bg-[#0a4c32]"
                data-testid="cookie-accept-all"
              >
                {t("cookie_banner.accept_all")}
              </button>
              <button
                type="button"
                onClick={rejectAll}
                className="px-5 py-2.5 rounded-full border border-[#0E2A47]/25 text-[#0E2A47] text-sm font-medium hover:bg-[#0E2A47]/5"
                data-testid="cookie-reject-all"
              >
                {t("cookie_banner.reject_all")}
              </button>
              {!advanced ? (
                <button
                  type="button"
                  onClick={() => setAdvanced(true)}
                  className="px-4 py-2 text-sm text-[#0E2A47]/75 underline underline-offset-2"
                  data-testid="cookie-manage"
                >
                  {t("cookie_banner.manage")}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={savePrefs}
                  className="px-5 py-2.5 rounded-full bg-[#0E2A47] text-white text-sm font-medium hover:bg-[#08203a]"
                  data-testid="cookie-save"
                >
                  {t("cookie_banner.save")}
                </button>
              )}
            </div>
          </div>
          <button
            type="button"
            aria-label={t("common.close")}
            onClick={rejectAll}
            className="hidden md:flex p-1 text-[#0E2A47]/50 hover:text-[#0E2A47]"
            data-testid="cookie-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
